import type {
  Tournament,
  TournamentFormData,
  TournamentResponse,
  MatchResult,
  Fixture,
  FeeInfo,
  Group,
  KnockoutTree,
  KnockoutNode,
  Team,
  TournamentSummary,
  SlotOccupancy,
  FixtureSibling,
  FixtureSiblingCancellation,
  SharedGrid,
  GridAvailableTeams,
  GridMove,
} from '@/types/tournament.model';
import api from '@/lib/api';
import {
  createSingleApiWithFields,
  createApiWithFields,
  HttpMethod,
} from '@/lib/api-base';
import { TOURNAMENT_FIELDS } from './tournament-fields.config';
import { FIXTURE_FIELDS } from './tournament-fixture-fields.config';
import type { FieldDefinition } from '@/types/fields-api.model';
import { FEE_FIELDS } from './tournament-fee-fields.config';
import { generateFields } from '../generators-api';
import { MATCH_FIELDS } from '../match/match-fields.config';
import { getDependentTeamLabels } from '@/utils/dependent-match';
import { groupLabel } from '@/utils/fixture-utils';

/**
 * Serializes a Date to ISO string with 00:00:00.000Z using the local calendar date
 * (year, month, day). No timezone conversion of the time part — always sends midnight UTC
 * for the chosen date.
 */
function dateToUtcMidnightString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}T00:00:00.000Z`;
}

/**
 * Serializes the signup deadline keeping the wall-clock time the manager picked. A string with no
 * time part falls back to 23:59, which is what every deadline meant before the hour became editable.
 *
 * The `Z` suffix is deliberately a lie: the backend parses this into a `LocalDateTime`, drops the
 * offset and treats the value as the club's wall clock, converting it to UTC itself
 * (`TournamentService` -> `getMatchUtils.convertLocalDateToUtc`). Sending a real UTC instant here
 * would get converted a second time and shift the deadline by the club's offset.
 */
function toClubWallClockString(value: Date | string): string | null {
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const hh = String(value.getHours()).padStart(2, '0');
    const mm = String(value.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}:00.000Z`;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value);
  if (!match) return null;
  const [, y, m, day, hh, mm] = match;
  return `${y}-${m}-${day}T${hh ?? '23'}:${mm ?? '59'}:00.000Z`;
}

/** Extracts YYYY-MM-DD from an ISO or date-only string without timezone shift */
function parseDateStringToUtcMidnight(s: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!match) return null;
  const [, y, m, day] = match;
  return `${y}-${m}-${day}T00:00:00.000Z`;
}

/**
 * Replaces date fields for API payloads: start/end at 00:00, signupDeadline at the hour the
 * manager picked. Accepts Date or ISO/date string so create and update always send the same format.
 */
function serializeTournamentDateFields<T extends Record<string, unknown>>(
  data: T,
): T {
  const out = { ...data } as T;
  const record = out as Record<string, unknown>;
  const raw = data as Record<string, unknown>;

  const startVal = raw.startDate;
  if (startVal instanceof Date) {
    record.startDate = dateToUtcMidnightString(startVal);
  } else if (typeof startVal === 'string' && startVal) {
    const normalized = parseDateStringToUtcMidnight(startVal);
    if (normalized) record.startDate = normalized;
  }

  const endVal = raw.endDate;
  if (endVal instanceof Date) {
    record.endDate = dateToUtcMidnightString(endVal);
  } else if (typeof endVal === 'string' && endVal) {
    const normalized = parseDateStringToUtcMidnight(endVal);
    if (normalized) record.endDate = normalized;
  }

  const signupVal = raw.signupDeadlineDate;
  if (signupVal instanceof Date || (typeof signupVal === 'string' && signupVal)) {
    const normalized = toClubWallClockString(signupVal);
    if (normalized) record.signupDeadlineDate = normalized;
  }

  return out;
}

// Helper functions using the field configurations with different HTTP methods
const getTournamentsWithFields = createApiWithFields<
  Tournament[],
  typeof TOURNAMENT_FIELDS
>('/tournaments/by-manager', TOURNAMENT_FIELDS, HttpMethod.GET);

const createTournamentWithFields = createSingleApiWithFields<
  Tournament,
  typeof TOURNAMENT_FIELDS
>('/tournaments', TOURNAMENT_FIELDS, HttpMethod.POST);

const updateTournamentWithFields = createSingleApiWithFields<
  Tournament,
  typeof TOURNAMENT_FIELDS
>('/tournaments', TOURNAMENT_FIELDS, HttpMethod.PATCH);

const getTournamentByIdWithFields = (id: string) =>
  createSingleApiWithFields<Tournament, typeof TOURNAMENT_FIELDS>(
    `/tournaments/manager/${id}`,
    TOURNAMENT_FIELDS,
    HttpMethod.GET,
  );

const getFixedFeeWithFields = createSingleApiWithFields<
  FeeInfo,
  typeof FEE_FIELDS
>('/checkout/getmatch/fee', FEE_FIELDS, HttpMethod.GET);

export const uploadImage = async (file: File, format = 'BANNER') => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/images/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    params: {
      format,
    },
  });
  return response.data;
};

/**
 * Gets the timezone from the first club's address for the creation payload.
 * Sent at root level so the backend knows the tournament's timezone.
 */
function getTimezoneFromFirstClub(
  data: TournamentFormData,
): string | undefined {
  const firstClub = data.clubs?.[0];
  const address = firstClub?.address as { timezone?: string } | undefined;
  return address?.timezone;
}

export const createTournament = async (
  tournamentData: TournamentFormData,
  fields: FieldDefinition = TOURNAMENT_FIELDS.FULL,
) => {
  const payload = serializeTournamentDateFields(
    tournamentData as Record<string, unknown>,
  ) as Record<string, unknown>;
  const timezone = getTimezoneFromFirstClub(tournamentData);
  if (timezone !== undefined) {
    payload.timezone = timezone;
  }
  return await createTournamentWithFields(fields, payload);
};

export const getTournaments = async (
  fields: (typeof TOURNAMENT_FIELDS)[keyof typeof TOURNAMENT_FIELDS] = TOURNAMENT_FIELDS.LIST,
): Promise<TournamentResponse> => {
  const tournaments = await getTournamentsWithFields(fields);
  return { content: tournaments as Tournament[] };
};

export const getTournamentById = async (
  tournamentId: string,
  fieldsScope: keyof typeof TOURNAMENT_FIELDS = 'FULL',
): Promise<Tournament> => {
  const getTournamentById = getTournamentByIdWithFields(tournamentId);
  const tournament = await getTournamentById(TOURNAMENT_FIELDS[fieldsScope]);
  return tournament as unknown as Tournament;
};

export const updateTournament = async (
  tournamentId: string,
  updateData: Partial<TournamentFormData>,
  fields: FieldDefinition = TOURNAMENT_FIELDS.FULL,
): Promise<Tournament> => {
  const payload = serializeTournamentDateFields({
    id: tournamentId,
    ...updateData,
  } as Record<string, unknown>);
  return await updateTournamentWithFields(fields, payload);
};

export const getFixedFeeAmount = async <
  T extends keyof typeof FEE_FIELDS = 'MINIMAL',
>(
  fieldsScope: T = 'MINIMAL' as T,
): Promise<number> => {
  try {
    const fields = FEE_FIELDS[fieldsScope];
    const response = await getFixedFeeWithFields(fields);
    return response.fee;
  } catch (error) {
    console.error('Error fetching fixed fee amount:', error);
    throw new Error(
      'No se pudo obtener la información de tarifas. Por favor, intente nuevamente más tarde.',
    );
  }
};

export const setMatchResult = async (
  tournamentId: string,
  matchId: string,
  result: MatchResult,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const response = await api.post(
    `/tournaments/${tournamentId}/matches/${matchId}/results`,
    result,
  );
  return response.data;
};

export const editMatchResult = async (
  tournamentId: string,
  matchId: string,
  result: MatchResult,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const response = await api.put(
    `/tournaments/${tournamentId}/matches/${matchId}/results`,
    result,
  );
  return response.data;
};

export const updateMatchDateTime = async (
  tournamentId: string,
  matchId: string,
  startTime: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  // Validate parameters
  if (!tournamentId || !matchId || !startTime) {
    throw new Error(
      `Missing required parameters: tournamentId=${tournamentId}, matchId=${matchId}, startTime=${startTime}`,
    );
  }

  const response = await api.patch(
    `/matches/${matchId}/${tournamentId}`,
    null,
    {
      params: {
        startTime,
        fields: generateFields(MATCH_FIELDS.MINIMAL),
      },
    },
  );
  return response.data;
};

export const cancelTournament = async (
  tournamentId: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    await api.patch(`/tournaments/${tournamentId}/cancel`, null, {
      params: { fields: generateFields({ id: true }) },
    });

    return {
      success: true,
      message: 'Torneo eliminado exitosamente',
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Error deleting tournament:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error al eliminar el torneo',
    };
  }
};

export default api;

/**
 * The qualifier label for each still-undefined slot of a tournament's bracket, keyed by matchId:
 * `{ team1: "2° Grupo B", team2: "1° Grupo C" }`.
 *
 * The backend already resolves these — `TournamentFacade.convertAndEnrichKnockoutNode` reads the
 * node's `bracketSlot` ("zone:position:zone:position") and renders it — so this reads that
 * endpoint instead of duplicating the rule on the client.
 *
 * **How the label travels is a wart worth knowing about:** the backend has no field for it, so it
 * smuggles the label in as a *fake player* on the placeholder team (`players: [{ name: label }]`,
 * with no id). That works for the bracket view, which just joins player names. If the backend ever
 * replaces that trick with a real field, this function silently returns nothing and the grid falls
 * back to "Equipo 1"/"Equipo 2" — no error, no warning. That is the tradeoff for not changing the
 * backend contract.
 *
 * Only leaf nodes carry a `bracketSlot`, so semifinals and finals are never labelled.
 */
export const getKnockoutPlaceholderLabels = async (
  tournamentId: string,
): Promise<Map<string, { team1?: string; team2?: string }>> => {
  const response = await api.get(
    `/tournaments/${tournamentId}/fixture/knockout`,
    { params: { fields: generateFields(FIXTURE_FIELDS.FULL) } },
  );

  const labels = new Map<string, { team1?: string; team2?: string }>();

  // A team that carries the injected label has exactly one "player" with a name and no id — a
  // real player always has one. Anything else is a resolved team and needs no label.
  const labelOf = (team?: Team): string | undefined => {
    const only = team?.players?.length === 1 ? team.players[0] : undefined;
    return only && !only.id && only.name ? only.name : undefined;
  };

  const visit = (node?: KnockoutNode) => {
    if (!node) return;
    const matchId = node.match?.id;
    if (matchId) {
      const team1 = labelOf(node.match?.team1);
      const team2 = labelOf(node.match?.team2);
      if (team1 || team2) labels.set(matchId, { team1, team2 });
    }
    visit(node.leftChild);
    visit(node.rightChild);
  };

  visit(response.data?.rootNode);
  return labels;
};

/**
 * Labels for the still-undefined sides of a group of 4's dependent matches (the 2+2 format):
 * `"Ganador Partido 1"` / `"Perdedor Partido 2"`, keyed by matchId. Same shape as
 * `getKnockoutPlaceholderLabels`, so the grid merges both into one map.
 *
 * The grid's own payload doesn't carry `match.description`, where the backend keeps the
 * `DEPENDENT_MATCH` marker, so it comes from the groups fixture. The numbering is the source
 * match's position in its group — the same "Partido N" the group cards show. A match whose sides
 * are already resolved has no marker and gets no label.
 */
export const getGroupDependentLabels = async (
  tournamentId: string,
): Promise<Map<string, { team1?: string; team2?: string }>> => {
  const response = await requestGroups(tournamentId);
  const groups = parseGroupList(response.data) ?? [];

  const labels = new Map<string, { team1?: string; team2?: string }>();
  groups.forEach((group) => {
    const matchList = group.matchList ?? [];
    matchList.forEach((match) => {
      const sides = getDependentTeamLabels(match, matchList);
      if (match.id && (sides.team1 || sides.team2)) labels.set(match.id, sides);
    });
  });
  return labels;
};

/**
 * teamId -> `"Grupo B"` for every team placed in a group. Empty when the tournament has no
 * groups. Asks only for the team ids: the full groups fixture carries every match too.
 */
export const getTeamGroupLabels = async (
  tournamentId: string,
): Promise<Map<string, string>> => {
  const response = await requestGroups(tournamentId, {
    groupList: { teamList: { team: { id: true } } },
  });
  const groups = parseGroupList(response.data) ?? [];

  const labels = new Map<string, string>();
  groups.forEach((group, index) => {
    const label = groupLabel(index);
    group.teamList?.forEach(({ team }) => {
      if (team?.id) labels.set(team.id, label);
    });
  });
  return labels;
};

export const generateKnockoutStage = async (tournamentId: string) => {
  const response = await api.post(
    `/tournaments/${tournamentId}/fixture/knockout`,
  );
  return response.data;
};

export const activateTournamentFixture = async (tournamentId: string) => {
  const response = await api.post(
    `/tournaments/${tournamentId}/fixture/activate`,
  );
  return response.data as Fixture;
};

export const generateTournamentFixture = async (tournamentId: string) => {
  const response = await api.post(`/tournaments/${tournamentId}/fixture`);
  return response.data as Fixture;
};

/** The groups fixture request. Groups endpoint must always include isManager=true. */
const requestGroups = (
  tournamentId: string,
  fields: FieldDefinition = FIXTURE_FIELDS.FULL,
) =>
  api.get(`/tournaments/${tournamentId}/fixture/groups`, {
    params: {
      fields: generateFields(fields),
      isManager: true,
    },
  });

/**
 * The groups endpoint returns an array of groups; `{ groupList: [...] }` is accepted for backward
 * compatibility. `undefined` when the payload is neither.
 */
const parseGroupList = (data: unknown): Group[] | undefined => {
  if (Array.isArray(data)) return data as Group[];
  if (data && Array.isArray((data as Fixture).groupList)) return (data as Fixture).groupList;
  return undefined;
};

export const getTournamentFixture = async (tournamentId: string) => {
  // Fetch group stage and knockout stage from separate endpoints.
  // Groups endpoint must always include isManager=true.
  const [groupsResult, knockoutsResult] = await Promise.allSettled([
    requestGroups(tournamentId),
    api.get(`/tournaments/${tournamentId}/fixture/knockout`, {
      params: {
        fields: generateFields(FIXTURE_FIELDS.FULL),
      },
    }),
  ]);

  let groupList: Group[] | undefined;
  let knockoutTree: KnockoutTree | undefined;

  if (groupsResult.status === 'fulfilled') {
    groupList = parseGroupList(groupsResult.value.data);
  }

  if (knockoutsResult.status === 'fulfilled') {
    const data = knockoutsResult.value.data as { rootNode?: KnockoutTree['rootNode'] };
    if (data?.rootNode) {
      knockoutTree = { rootNode: data.rootNode };
    }
  }

  // If both succeeded, merge results (backward compatible with previous single-endpoint shape)
  if (groupList && knockoutTree) {
    return {
      groupList,
      knockoutTree,
    } as Fixture;
  }

  // If only groups loaded, return just groupList
  if (groupList) {
    return {
      groupList,
    } as Fixture;
  }

  // If only knockouts loaded, return just knockoutTree
  if (knockoutTree) {
    return {
      knockoutTree,
    } as Fixture;
  }

  // If neither endpoint is available, fall back to an empty fixture
  console.error(
    'Error fetching both groups and knockouts fixture endpoints for tournament',
    tournamentId,
  );
  return {} as Fixture;
};

export const deleteMatch = async (matchId: string): Promise<void> => {
  await api.delete(`/matches/${matchId}`);
};

export const getTournamentStats = async (): Promise<TournamentSummary> => {
  const response = await api.get<TournamentSummary>('/tournaments/me/stats');
  return response.data;
};

/** Swap two teams between groups; with an active fixture, neither team may have a loaded result. */
export const swapTeamsBetweenGroups = async (
  tournamentId: string,
  teamAId: string,
  teamBId: string,
): Promise<{ id: string }> => {
  const response = await api.post<{ id: string }>(
    `/tournaments/${tournamentId}/groups/swap/${teamAId}/${teamBId}`,
    null,
    {
      params: {
        fields: generateFields({ id: true }),
      },
    },
  );
  return response.data;
};

/**
 * Swap the spots of two teams in a knockout-only bracket (first-round match or bye);
 * neither team may have a loaded result, and they can't share their first match.
 */
export const swapTeamsInBracket = async (
  tournamentId: string,
  teamAId: string,
  teamBId: string,
): Promise<{ id: string }> => {
  const response = await api.post<{ id: string }>(
    `/tournaments/${tournamentId}/knockout/swap/${teamAId}/${teamBId}`,
    null,
    {
      params: {
        fields: generateFields({ id: true }),
      },
    },
  );
  return response.data;
};

/**
 * Slots where the fixture has forced matches, with everything booked into each one. Only the
 * organizer's own view needs it, so it is fetched on demand instead of riding along the fixture.
 */
export const getFixtureSlotOccupancy = async (
  tournamentId: string,
): Promise<SlotOccupancy[]> => {
  const response = await api.get<SlotOccupancy[]>(
    `/tournaments/${tournamentId}/fixture/slot-occupancy`,
  );
  return response.data;
};

/**
 * Tournaments whose fixture gets generated together with this one's. The backend picks them with
 * the same per-format filter the generation uses, so the list matches what will be created. With
 * `includeNotGenerated`, a LONG also gets the grid siblings that are left out, each with its reason.
 * With `includeToCancel`, it also lists the siblings below their minimum that get cancelled before
 * generating (`toCancel: true`); without it they are not listed, so an older caller never shows
 * them as generated.
 */
export const getFixtureSiblings = async (
  tournamentId: string,
  {
    includeNotGenerated = false,
    includeToCancel = false,
  }: { includeNotGenerated?: boolean; includeToCancel?: boolean } = {},
): Promise<FixtureSibling[]> => {
  // Only the flags that are on go out (axios drops undefined), so a plain call keeps hitting the bare URL.
  const params = {
    includeNotGenerated: includeNotGenerated || undefined,
    includeToCancel: includeToCancel || undefined,
  };
  const response = await api.get<FixtureSibling[]>(
    `/tournaments/${tournamentId}/fixture/siblings`,
    { params },
  );
  return response.data;
};

/**
 * Cancels the given siblings (full refund, no penalty) so this tournament's fixture can be
 * generated. The backend only cancels the ones still below their minimum and answers with one
 * result per id, so a sibling that filled up in the meantime comes back SKIPPED instead of failing
 * the request.
 */
export const cancelFixtureSiblings = async (
  tournamentId: string,
  siblingIds: string[],
): Promise<FixtureSiblingCancellation[]> => {
  const response = await api.post<FixtureSiblingCancellation[]>(
    `/tournaments/${tournamentId}/fixture/siblings/cancel`,
    { siblingIds },
  );
  return response.data;
};

/**
 * The tournament's shared court grid: every slot in its blocks and the matches that fall
 * into them, both its own and those from sibling tournaments sharing those blocks.
 */
export const getSharedGrid = async (
  tournamentId: string,
): Promise<SharedGrid> => {
  const response = await api.get<SharedGrid>(
    `/tournaments/${tournamentId}/shared-grid`,
  );
  return response.data;
};

/**
 * The pairs of the grid's tournaments (this one and its siblings) that declared they can play at
 * `date` (`YYYY-MM-DD`) and `time` (`HH:mm`, the club's local time), grouped by tournament.
 *
 * Only looks at what each pair declared: it isn't crossed with the matches, so a pair already
 * playing at that time still comes back. Tournaments with no such pair are left out.
 */
export const getGridAvailableTeams = async (
  tournamentId: string,
  date: string,
  time: string,
): Promise<GridAvailableTeams[]> => {
  const response = await api.get<GridAvailableTeams[]>(
    `/tournaments/${tournamentId}/shared-grid/available-teams`,
    { params: { date, time } },
  );
  return response.data;
};

/**
 * Applies all the moves together and returns the recalculated grid.
 *
 * Sent as a batch, not one by one, on purpose: the single-move endpoint rejects the
 * intermediate step of a swap (a team with two matches at the same time) even when the final
 * state is valid, and it also notifies players on every call.
 */
export const saveGridMoves = async (
  tournamentId: string,
  moves: GridMove[],
): Promise<SharedGrid> => {
  const response = await api.post<SharedGrid>(
    `/tournaments/${tournamentId}/grid/moves`,
    { moves },
  );
  return response.data;
};
